/**
 * Modal shell for the tech/university pages (same behaviour as the IP Intel
 * one, scoped to `.tpg`): dimmed backdrop, white card, × top-right. Escape
 * and a backdrop click close it unless `locked`. Focus lands on the first
 * field, Tab stays inside, and focus returns to the opener on close.
 * Must render inside a `.tpg` root (for the styles).
 */

import React, { useEffect, useRef } from 'react';

export type ModalCloseVia = 'close' | 'escape' | 'backdrop';

interface Props {
  labelledBy: string;
  onClose: (via: ModalCloseVia) => void;
  locked?: boolean;
  maxWidth?: number;
  children: React.ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const TpModal: React.FC<Props> = ({ labelledBy, onClose, locked, maxWidth = 460, children }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tabbables = (): HTMLElement[] =>
      Array.from(cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || [])
        .filter(el => el.tabIndex >= 0 && !el.closest('.tpg-hp') && !el.hidden && el.getClientRects().length > 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !lockedRef.current) { closeRef.current('escape'); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const list = tabbables();
      if (!list.length) { e.preventDefault(); cardRef.current.focus(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && cardRef.current.contains(active);
      if (e.shiftKey && (!inside || active === first || active === cardRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || active === last)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const first = cardRef.current?.querySelector<HTMLElement>('input:not([tabindex="-1"]), select, textarea, button:not(.tpg-modal-close)');
    (first || cardRef.current)?.focus();
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
      try { if (opener && document.contains(opener)) opener.focus({ preventScroll: true }); } catch { /* ignore */ }
    };
  }, []);

  return (
    <div
      className="tpg-modal-backdrop"
      onMouseDown={e => { if (e.target === e.currentTarget && !locked) onClose('backdrop'); }}
    >
      <div
        ref={cardRef}
        className="tpg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        style={{ maxWidth }}
      >
        <button
          type="button"
          className="tpg-modal-close"
          aria-label="Close"
          onClick={() => onClose('close')}
          disabled={locked}
          style={locked ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
        >×</button>
        {children}
      </div>
    </div>
  );
};
